import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
  });
  if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.message || 'Request failed'); }
  return response.json();
}

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const riskColors: Record<string, string> = {
  exceeded: 'bg-red-100 text-red-800 border-red-200',
  critical: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  warning: 'bg-orange-50 text-orange-700 border-orange-200',
  low: 'bg-green-50 text-green-700 border-green-200',
  none: 'bg-gray-50 text-gray-500 border-gray-200',
  no_tax: 'bg-gray-50 text-gray-400 border-gray-100',
};

const riskLabels: Record<string, string> = {
  exceeded: 'EXCEEDED',
  critical: 'WARNING',
  warning: 'Monitor',
  low: 'Low',
  none: 'None',
  no_tax: 'No Tax',
};

export default function NexusReportPage() {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'risk' | 'wholesale'>('risk');
  const [editingId, setEditingId] = useState<string | null>(null);

  // Period selector — YTD (default, full year-to-date) or a specific quarter.
  // Sends ?period=quarter&year=YYYY&quarter=N to the report endpoint; backend
  // shrinks the "primary" column to that quarter. Rolling-12mo column is
  // unaffected (always 12 months back from today).
  const now = new Date();
  const [period, setPeriod] = useState<'ytd' | 'quarter'>('ytd');
  const [year, setYear] = useState<number>(now.getFullYear());
  const [quarter, setQuarter] = useState<number>(Math.floor(now.getMonth() / 3) + 1);

  useEffect(() => { loadReport(); }, [period, year, quarter]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = period === 'quarter'
        ? `?period=quarter&year=${year}&quarter=${quarter}`
        : `?period=ytd&year=${year}`;
      const data = await apiRequest(`/accounting/nexus-report${params}`);
      setReport(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;
  if (!report) return <div className="text-center py-12 text-gray-500">Failed to load report</div>;

  const items = report.report || [];
  const filtered = filter === 'all' ? items
    : filter === 'risk' ? items.filter((s: any) => s.maxPercent > 0 && s.hasSalesTax)
    : items.filter((s: any) => s.wholesaleCounts && s.hasSalesTax);

  const exceededCount = items.filter((s: any) => s.risk === 'exceeded').length;
  const warningCount = items.filter((s: any) => s.risk === 'critical').length;
  const totalYtdSales = items.reduce((s: number, r: any) => s + r.ytdSales, 0);
  const statesWithSales = items.filter((s: any) => s.ytdSales > 0).length;
  const periodLabel = report.periodLabel || (report.period === 'quarter' ? `Q${report.quarter} ${report.year}` : `${report.year || now.getFullYear()} YTD`);
  // When viewing a single quarter, the column header + summary label switches
  // from "YTD" to "Q1/Q2/etc." for clarity. Underlying data shape is unchanged.
  const periodColLabel = report.period === 'quarter' ? `Q${report.quarter}` : 'YTD';
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  const handleSave = async (id: string, field: string, value: any) => {
    try {
      await apiRequest(`/accounting/nexus-thresholds/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [field]: value }),
      });
      loadReport();
    } catch (err: any) { alert(err.message); }
    setEditingId(null);
  };

  return (
    <div>
      <PageHeader
        title="Economic Nexus Tracker"
        subtitle="Sales tax economic nexus risk by state — tracks when registration may be required"
      />

      {/* Period selector — YTD or single quarter */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setPeriod('ytd')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${period === 'ytd' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            YTD
          </button>
          <button
            onClick={() => setPeriod('quarter')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${period === 'quarter' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Quarterly
          </button>
        </div>
        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value, 10))}
          className="border rounded px-3 py-2 text-sm"
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {period === 'quarter' && (
          <select
            value={quarter}
            onChange={(e) => setQuarter(parseInt(e.target.value, 10))}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value={1}>Q1 (Jan–Mar)</option>
            <option value={2}>Q2 (Apr–Jun)</option>
            <option value={3}>Q3 (Jul–Sep)</option>
            <option value={4}>Q4 (Oct–Dec)</option>
          </select>
        )}
        <span className="text-sm text-gray-500">Showing: <span className="font-semibold text-gray-900">{periodLabel}</span></span>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="text-center">
          <p className="text-2xl font-bold text-red-600">{exceededCount}</p>
          <p className="text-sm text-gray-500">Threshold Exceeded</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-yellow-600">{warningCount}</p>
          <p className="text-sm text-gray-500">At 80%+ Warning</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-blue-600">{statesWithSales}</p>
          <p className="text-sm text-gray-500">States With Sales</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">{fmt(totalYtdSales)}</p>
          <p className="text-sm text-gray-500">{periodColLabel} Total Sales</p>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {([
            { key: 'risk' as const, label: 'States With Sales' },
            { key: 'wholesale' as const, label: 'Wholesale Counts (B2B Risk)' },
            { key: 'all' as const, label: 'All States' },
          ]).map(t => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === t.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-gray-400">
          {periodColLabel}: {report.ytdStart}{report.ytdEnd ? ` to ${report.ytdEnd}` : ''} — Rolling 12mo: {report.rollingStart} — Generated: {new Date(report.generatedAt).toLocaleString()}
        </div>
      </div>

      {/* Report Table */}
      <Card>
        <div className="overflow-x-auto -mx-1">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Threshold</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{periodColLabel} Sales</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{periodColLabel} %</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rolling 12mo</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rolling %</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Orders</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Txn Threshold</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Wholesale Counts</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((s: any) => {
                const rowBg = s.risk === 'exceeded' ? 'bg-red-50' : s.risk === 'critical' ? 'bg-yellow-50' : '';
                return (
                  <tr key={s.id} className={`${rowBg} hover:bg-gray-50`}>
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                      {s.state} <span className="text-gray-400 text-xs">({s.stateCode})</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${riskColors[s.risk]}`}>
                        {riskLabels[s.risk]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {s.thresholdAmount > 0 ? fmt(s.thresholdAmount) : <span className="text-gray-400">N/A</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {s.ytdSales > 0 ? fmt(s.ytdSales) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {s.ytdPercent > 0 ? (
                        <span className={`font-bold ${s.ytdPercent >= 100 ? 'text-red-600' : s.ytdPercent >= 80 ? 'text-yellow-600' : 'text-gray-600'}`}>
                          {s.ytdPercent.toFixed(1)}%
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {s.rollingSales > 0 ? fmt(s.rollingSales) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {s.rollingPercent > 0 ? (
                        <span className={`font-bold ${s.rollingPercent >= 100 ? 'text-red-600' : s.rollingPercent >= 80 ? 'text-yellow-600' : 'text-gray-600'}`}>
                          {s.rollingPercent.toFixed(1)}%
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-600">
                      {(s.ytdOrders || 0) > 0 ? s.ytdOrders : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center text-gray-600 text-xs">
                      {s.transactionCount ? (
                        <span>{s.transactionCount} <span className="text-gray-400">({s.transactionLogic})</span></span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {!s.hasSalesTax ? (
                        <span className="text-gray-300">N/A</span>
                      ) : s.wholesaleCounts ? (
                        <span className="text-red-600 font-bold">YES</span>
                      ) : (
                        <span className="text-green-600">NO</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs max-w-[200px] truncate" title={s.notes}>
                      {s.notes || '—'}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-400">No states match this filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Legend */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border text-xs text-gray-500 space-y-1">
        <p><strong>How to read this report:</strong> Sales are grouped by the shipping state of each order (or dealer billing state if no ship-to address).</p>
        <p><span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-bold">EXCEEDED</span> = threshold met or exceeded, registration likely required.</p>
        <p><span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 font-bold">WARNING</span> = 80%+ of threshold, approaching nexus.</p>
        <p><strong>Wholesale Counts = YES</strong> means B2B wholesale sales count toward the threshold even though no tax is collected. This is the key column for Keder.</p>
        <p><strong>Txn Threshold (AND)</strong> = both dollar AND transaction thresholds must be met. <strong>(OR)</strong> = either triggers nexus.</p>
        <p className="text-gray-400 mt-2">Thresholds last verified: April 2026. Update thresholds via the database or API as laws change.</p>
      </div>
    </div>
  );
}
